import { getApiErrorCode, getApiErrorText, getErrorMessage } from '@nebula/shared/utils'

/**
 * Decide o que a tela faz com uma falha de `POST /end-customer/instances`.
 *
 * Existe como função pura porque a ORDEM das regras é o que importa e é o que
 * regride fácil: a recusa por VPC sem Cloud Gateway (issue #727) precisa ser
 * resolvida pelo `code` estruturado **antes** das heurísticas de substring, já
 * que a mensagem dela contém "VPC" e cairia no ramo que abre o modal "Criar
 * VPC" — o cliente veria um formulário de criar outra rede em vez do motivo da
 * recusa.
 */

export type AcaoDeErroDeCriacao =
  | {
      tipo: 'alerta'
      title: string
      message: string
      variant: 'warning' | 'error'
      /** Recarregar a lista de VPCs: ela estava velha e mentiu sobre o gateway. */
      recarregarVpcs?: boolean
    }
  | { tipo: 'abrirCriarVpc' }

export function decidirAcaoDeErroDeCriacao(error: any): AcaoDeErroDeCriacao {
  const textoDaApi = getApiErrorText(error)
  const errorMessage = textoDaApi ?? getErrorMessage(error)
  const statusCode = error?.response?.status

  // 1º: recusa identificada por código — nunca por texto.
  if (getApiErrorCode(error) === 'vpc_without_gateway') {
    return {
      tipo: 'alerta',
      title: 'VPC sem Cloud Gateway',
      message: `${errorMessage}\n\nA lista de VPCs foi atualizada — tente de novo para confirmar a criação nessa rede.`,
      variant: 'warning',
      recarregarVpcs: true,
    }
  }

  if (statusCode === 402) {
    return {
      tipo: 'alerta',
      title: 'Problema com Conta de Crédito',
      // Sem texto da API, `errorMessage` seria "Request failed with status code
      // 402" — num 402 a orientação específica vale mais. A régua é o TEXTO que
      // o servidor mandou, não a existência do corpo.
      message:
        textoDaApi ??
        'Não foi possível processar o pagamento. Verifique sua conta de crédito com o reseller.',
      variant: 'warning',
    }
  }

  if (errorMessage.toLowerCase().includes('association')) {
    return {
      tipo: 'alerta',
      title: 'Associação com Reseller Necessária',
      message:
        `Para comprar usando conta de crédito, você precisa ter uma associação ativa com o reseller.\n\n` +
        `Opções disponíveis:\n` +
        `• Use cartão de crédito para comprar sem associação\n` +
        `• Solicite uma associação com o reseller na página "Resellers"`,
      variant: 'warning',
    }
  }

  if (
    errorMessage.toLowerCase().includes('vpc') ||
    errorMessage.toLowerCase().includes('rede privada')
  ) {
    return { tipo: 'abrirCriarVpc' }
  }

  return {
    tipo: 'alerta',
    title: 'Erro ao Criar VM',
    message: errorMessage,
    variant: 'error',
  }
}
